<template>
    <div :class="[inheritedClass, {'has-validation-error': error }]">
        <div class="relative w-full"
             :class="{ hasValue: hasValue }">
            <textarea :id="id"
                      v-autofocus="autofocus"
                      :name="id"
                      class="c-input-textarea__input h-full w-full z-0 bg-gray-300 text-black outline-none"
                      :class="inputClasses"
                      :value="modelValue"
                      :rows="rows"
                      cols="10"
                      v-bind="inputAttributes"
                      @input="changedInput">
                      </textarea>
            <label v-if="labelText"
                   :for="id"
                   class="c-input-textarea__label w-full truncate absolute currentColor text-gray-800 font-medium leading-none">
                {{ labelText }}
            </label>
            <div v-if="maxCounter && !error"
                 class="text-12 font-medium px-18 pb-5 pt-3">
                {{ maxCounter }}
            </div>
        </div>
        <div v-if="showErrors && error"
             role="alert">
            <ol>
                <li :key="error"
                    class="text-red-500 text-12 font-medium px-18 pb-5 pt-3">
                    {{ error }}
                </li>
            </ol>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, useAttrs, toRef, watch } from 'vue';
import dictionaryService from '@/core/translation/dictionary.service';
import * as z from 'zod';
import useFieldValidation from '@/project/form/useFieldValidation';
import {
    getMaxLengthFromZodSchema,
    validationRuleRequired
} from '@/project/form/zodValidations';

interface Props {
    rules?: z.Schema<any>;
    required?: boolean;
    label?: string;
    name: string;
    autofocus?: boolean;
    showErrors?: boolean;
    showMaxCounter?: boolean;
    rows?: number;
    inputClasses?: string;
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    rules: undefined,
    required: undefined,
    autofocus: false,
    showErrors: true,
    showMaxCounter: false,
    rows: 5,
    inputClasses: 'resize-none',
});
const modelValue = defineModel<string>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: string | number | undefined | null): void;
    (e: 'update:error', value: string | number | undefined | null): void;
}>();

const constraints = computed((): z.Schema<any> => {
    if (props.required && !props.rules) {
        return validationRuleRequired();
    }
    if (props.rules) {
        return props.rules;
    }
    return z.any();
});

const { field, validate } = useFieldValidation(props.name, props.label, constraints, toRef(modelValue));

defineExpose({ validate, field });

const attrs = useAttrs();

const { class: inheritedClass, ...inputAttributes } = attrs;

const id = computed(() => props.name + Math.random());

const isRequired = computed((): boolean => {
    return !!props.required;
});

const hasValue = computed((): boolean => {
    return modelValue.value !== '' && modelValue.value != null;
});

const labelText = computed((): string => {
    return isRequired.value ? `${props.label} *` : props.label;
});

const error = computed(() => {
    return field.error;
});

const maxCounter = computed((): string | null => {
    const max = props.showMaxCounter && props.rules ? getMaxLengthFromZodSchema(props.rules) : undefined;
    if (max) {
        const valueLength = modelValue.value?.length ?? 0;
        return dictionaryService.get('Validation.TextAreaCharsLeftCounter', [(max - valueLength).toString()]);
    }
    return null;
});

watch(() => error.value, (value) => {
    emit('update:error', value);
});

function changedInput(event: Event) {
    const target = event.target as HTMLTextAreaElement;
    emit('update:modelValue', target.value);

    if (field.validated && !field.valid) {
        validate();
    }
}
</script>

<style lang="less" scoped>
    .c-input-textarea__input {
        @apply rounded-8 min-w-0 shadow-input text-16 px-18 pt-14 pb-20 leading-normal placeholder-gray-825;
        transition: background-color ease-out 0.3s;
        vertical-align: middle;

        .hasValue & {
            outline: 0;
            padding-top: 1.7rem;

            & ~ .c-input-textarea__label {
                transform: translate(0, -100%) scale(0.8);
                padding-top: 1.1rem;
                padding-bottom: 0;
                top: 1.9rem;
            }
        }

        &.invalid {
            @apply border border-solid border-red-500 text-red-500;
        }

        &[disabled] {
            @apply text-gray-700;
        }

        .c-input--white & {
            @apply bg-white;

            &:focus {
                @apply bg-white;
            }
        }

        &:focus {
            @apply bg-gray-250;

            & ~ .c-input-textarea__label {
                @apply text-gray-825;
            }
        }
    }

    .c-input-textarea__label {
        @apply cursor-text text-12;
        transition: transform 0.3s ease-in-out, color ease-out 0.3s;
        transform-origin: left;
        left: 1.8rem;
        top: 1.5rem;
        width: calc(100% - 4rem);
        line-height: 1.2; // So letters like lowercase "g" are not cut off
        letter-spacing: 0.02rem;
    }
    @screen md {
        .c-input-textarea__input {
            @apply text-12;
        }
    }
</style>
